import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export interface AdminPaymentBinding {
  id: string;
  payment_attempt_id: string;
  attempt_id: string;
}

export interface AdminOrderDetail {
  id: string;
  status: string;
  amount_minor: number;
  currency: string;
  payment_attempt_id: string | null;
  payment_attempt_state: string | null;
  refunded_minor: number;
  created_at: string;
  updated_at: string;
  target_type: string;
  confirmation_result: string | null;
  payment_approved: boolean;
  payment: AdminPaymentBinding | null;
  refund_requests: {
    request_operation_id: string;
    amount_minor: number;
    completed_minor: number;
    currency: string;
    status: string;
    hold_reason: string | null;
  }[];
}

export interface AdminPaymentDetail {
  id: string;
  payment_attempt_id: string | null;
  status: string;
  amount_minor: number;
  currency: string;
  captured: boolean;
  status_changed_at: string;
  created_at: string;
  updated_at: string;
  order: {
    id: string;
    payment_id: string;
    payment_attempt_id: string;
    attempt_id: string;
  } | null;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const nullableText = (value: unknown): value is string | null => value === null || text(value);
const nullableUuid = (value: unknown): value is string | null => value === null || isUuid(value);
const amount = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0;
const currency = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Z]{3}$/.test(value);
const hasKeys = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).sort().join(',') === [...keys].sort().join(',');

function invalid(): ApiError {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '금융 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });
}

function parsePaymentBinding(value: unknown): AdminPaymentBinding | null {
  if (value === null) return null;
  if (
    !record(value) ||
    !hasKeys(value, ['id', 'payment_attempt_id', 'attempt_id']) ||
    !isUuid(value.id) ||
    !isUuid(value.payment_attempt_id) ||
    !isUuid(value.attempt_id)
  )
    throw invalid();
  return value as unknown as AdminPaymentBinding;
}

function parseRefundRequest(value: unknown, orderId: string) {
  if (
    !record(value) ||
    !hasKeys(value, [
      'order_id',
      'request_operation_id',
      'sequence',
      'amount_minor',
      'completed_minor',
      'currency',
      'status',
      'hold_reason',
      'created_at',
      'updated_at',
    ]) ||
    value.order_id !== orderId ||
    !isUuid(value.request_operation_id) ||
    !Number.isSafeInteger(value.sequence) ||
    Number(value.sequence) < 1 ||
    !amount(value.amount_minor) ||
    !amount(value.completed_minor) ||
    !currency(value.currency) ||
    !text(value.status) ||
    !nullableText(value.hold_reason) ||
    !isOffsetDateTime(value.created_at) ||
    !isOffsetDateTime(value.updated_at)
  )
    throw invalid();
  return {
    request_operation_id: value.request_operation_id,
    amount_minor: value.amount_minor,
    completed_minor: value.completed_minor,
    currency: value.currency,
    status: value.status,
    hold_reason: value.hold_reason,
  };
}

function parseOrder(value: unknown, requestedId: string): AdminOrderDetail {
  if (
    !record(value) ||
    !hasKeys(value, [
      'id',
      'status',
      'amount_minor',
      'currency',
      'payment_attempt_id',
      'payment_attempt_state',
      'refunded_minor',
      'created_at',
      'updated_at',
      'target_type',
      'confirmation_result',
      'payment_approved',
      'payment',
      'refund_requests',
    ]) ||
    value.id !== requestedId ||
    !text(value.status) ||
    !amount(value.amount_minor) ||
    !currency(value.currency) ||
    !nullableUuid(value.payment_attempt_id) ||
    !nullableText(value.payment_attempt_state) ||
    !amount(value.refunded_minor) ||
    !isOffsetDateTime(value.created_at) ||
    !isOffsetDateTime(value.updated_at) ||
    !text(value.target_type) ||
    !nullableText(value.confirmation_result) ||
    typeof value.payment_approved !== 'boolean' ||
    !Array.isArray(value.refund_requests)
  )
    throw invalid();
  const payment = parsePaymentBinding(value.payment);
  if (
    payment !== null &&
    (payment.payment_attempt_id !== value.payment_attempt_id ||
      payment.attempt_id !== value.payment_attempt_id)
  )
    throw invalid();
  return {
    ...(value as unknown as Omit<AdminOrderDetail, 'payment' | 'refund_requests'>),
    payment,
    refund_requests: value.refund_requests.map((item) => parseRefundRequest(item, requestedId)),
  };
}

function parsePayment(value: unknown, requestedId: string): AdminPaymentDetail {
  if (
    !record(value) ||
    !hasKeys(value, [
      'id',
      'payment_attempt_id',
      'status',
      'amount_minor',
      'currency',
      'captured',
      'status_changed_at',
      'created_at',
      'updated_at',
      'order',
      'refund_operations',
    ]) ||
    value.id !== requestedId ||
    !nullableUuid(value.payment_attempt_id) ||
    !text(value.status) ||
    !amount(value.amount_minor) ||
    !currency(value.currency) ||
    typeof value.captured !== 'boolean' ||
    !isOffsetDateTime(value.status_changed_at) ||
    !isOffsetDateTime(value.created_at) ||
    !isOffsetDateTime(value.updated_at) ||
    !(
      value.order === null ||
      (record(value.order) &&
        hasKeys(value.order, ['id', 'payment_id', 'payment_attempt_id', 'attempt_id']) &&
        isUuid(value.order.id) &&
        isUuid(value.order.payment_id) &&
        isUuid(value.order.payment_attempt_id) &&
        isUuid(value.order.attempt_id))
    ) ||
    !Array.isArray(value.refund_operations)
  )
    throw invalid();
  return value as unknown as AdminPaymentDetail;
}

export async function readAdminOrderDetail(
  client: ServiceClient,
  orderId: string,
  signal?: AbortSignal,
): Promise<AdminOrderDetail> {
  if (!isUuid(orderId)) throw invalid();
  return parseOrder(await client.get<unknown>(`admin/orders/${orderId}`, { signal }), orderId);
}

export async function readAdminPaymentDetail(
  client: ServiceClient,
  paymentId: string,
  signal?: AbortSignal,
): Promise<AdminPaymentDetail> {
  if (!isUuid(paymentId)) throw invalid();
  return parsePayment(
    await client.get<unknown>(`admin/payments/${paymentId}`, { signal }),
    paymentId,
  );
}
